import React from 'react';
import TermsLayout from './TermsLayout';

function TermsOfService() {
  const sections = [
    {
      title: "1. Agreement to Terms",
      content: `By creating a Zella account, accessing the mobile application, or using the web dashboard, you agree to be bound by these Terms of Service ("Terms"), our Privacy Policy, our Acceptable Use Policy, our Cookie Policy, and our Data Processing Agreement (together, the "Agreement").

If you do not agree to these Terms, do not use Zella.

These Terms constitute a legally binding agreement between you (the "User") and Zella Wellbeing ("Zella", "we", "us", or "our").

Service Provider:
Ursell Property Limited (trading as Zella Fatigue & Wellbeing)
Carpenters House, 24 The Street, Leighterton, United Kingdom, GL8 8UN
Company number: 11056504
Email: legal@zellawellbeing.com
Website: https://zellawellbeing.com

Carpenters House, 24 The Street, Leighterton, GL8 8UN
Company number: 11056504

Who These Terms Apply To:
1. Mobile App Users (Employees): Individuals who use the Zella mobile app to submit wellbeing surveys
2. Dashboard Users (Administrators): Company administrators who use the web dashboard to manage teams and view analytics
3. Organisations: Companies or entities that register to use Zella's services — including both business customers and, where applicable, individual users who may be treated as consumers under UK law

Data Processing Agreement:
By creating an organisation account, the organisation agrees to the Data Processing Agreement available at zellawellbeing.com/terms/data-processing-agreement, which forms part of these Terms and is required by UK GDPR Article 28.

Changes to Terms:
We reserve the right to modify these Terms at any time. Material changes will be communicated with 30 days notice. Continued use after changes constitutes acceptance.`
    },
    {
      title: "2. Service Description and Critical Safety Limitations",
      content: `What Zella Provides:

Zella is a workplace wellbeing monitoring platform consisting of:

Mobile Application (Employee-Facing):
- Survey interface for submitting wellbeing data (happiness, tiredness, reaction time)
- Personal tiredness score calculations
- Survey history and submission tracking

Web Dashboard (Administrator-Facing):
- Team management and employee approval
- Aggregated team-level analytics and reports
- Billing and subscription management
- Account and organisation settings

CRITICAL — What Zella Does NOT Provide and Must Not Be Used For:

Zella does not make, and must not be used to make, employment decisions, staffing decisions, scheduling decisions, fitness-for-work assessments, transportation decisions, operational decisions, or health and safety decisions. All such decisions remain solely the responsibility of the organisation and its qualified personnel.

Zella does not provide:
- Medical diagnosis, clinical assessment, or treatment of any kind
- Professional health, legal, or safety advice
- Individual employee performance tracking or assessment
- Real-time alerting or emergency response capability
- Guaranteed accuracy of any fatigue prediction, score, or estimate

Algorithmic Scores and Predictions:

Any scores, analytics, recommendations, predictions, or estimates generated by Zella — including tiredness scores and team averages — are algorithmic estimates only. They may be inaccurate, incomplete, based on limited data, or unsuitable for particular circumstances. They must not be used as the sole or primary basis for any decision affecting workplace safety or employee welfare.

Organisations remain solely responsible for:
- Conducting proper workplace risk assessments
- Complying with all applicable health and safety legislation
- Making staffing, scheduling, and fitness-for-work decisions using qualified professional judgment
- Ensuring the wellbeing of employees through means appropriate to their specific circumstances

Zella is one optional input among many. It is not a medical device, not a substitute for professional judgment, and not a guarantee of safety or accident prevention.`
    },
    {
      title: "3. Account Registration and Eligibility",
      content: `To use Zella, you must:
- Be at least 18 years of age
- Have authority from your organisation to create an account (for admins) or permission to participate (for employees)
- Provide accurate and complete registration information
- Not be prohibited from using the service under applicable law
- Agree to comply with these Terms and our Acceptable Use Policy

Account Types:

Employee Accounts (Mobile App):
Created by individual employees using a valid company access code, subject to administrator approval. One account per person.

Administrator Accounts (Web Dashboard):
Created by company representatives, responsible for team management, data access, and billing.

Account Security:
You are responsible for maintaining confidentiality of your password, all activities under your account, and notifying us immediately of any unauthorised access at legal@zellawellbeing.com.

Prohibited Account Actions:
- Creating multiple accounts for the same person
- Sharing account credentials
- Using another person's account
- Creating accounts with false information
- Automated account creation (bots or synthetic users)

Export Controls and Sanctions:
You may not use Zella in violation of applicable export control, sanctions, or trade restriction laws. You represent that you are not located in a country subject to UK, EU, or US sanctions, and that you are not on any government prohibited party list.`
    },
    {
      title: "4. Billing and Payment",
      content: `Free Tier:

Zella is free for up to 10 approved employees per company. No payment details are required for the free tier.

Paid Subscriptions:

Beyond 10 employees, companies pay per additional employee:
- Monthly: £5 per employee per month
- Annual: £54 per employee per year (equivalent to £4.50/month — a 10% saving)

Prices are subject to change with 30 days notice. Any price change will not affect the current billing period.

Scheduled Payments:

Administrators can create one or more independent "scheduled payments," each covering a specified number of additional employees at a fixed billing interval. For example, a company might create one payment for 20 employees at £100/month and another for 10 employees at £50/month. Each scheduled payment operates independently and can be cancelled or reduced separately.

How Billing Works:

Payment is taken immediately when a scheduled payment is first set up, then automatically again every 30 days (monthly) or annually. All payments are taken in advance.

All payments are processed by Stripe. Card data never touches Zella's servers. Zella stores only the card brand and last 4 digits for display purposes. Stripe handles PCI DSS compliance for all card data.

Failed Payments:

If a payment fails, the employees covered by that scheduled payment are removed from the company's approved headcount immediately. If this causes the company's total approved headcount to exceed their paid limit, access to Reports, Analytics, and Overview alerts will be locked until the payment is resolved. All other platform features continue to work.

Zella does not pursue failed payments as debt. Failed invoices are voided. Access is simply removed until a new payment is made.

Cancellation:

You may cancel a scheduled payment at any time. On cancellation, you retain access for the remainder of the period you have already paid for. When that period ends, the employee limit drops accordingly.

Refund Policy:

Except where required by applicable law, payments are non-refundable. Payments are taken in advance and access continues for the full paid period regardless of cancellation. This applies to both monthly and annual plans.

Consumer Waiver (Individual Purchasers):

If you are an individual purchasing Zella outside the course of a business, you may have a statutory 14-day right to cancel under the Consumer Contracts Regulations 2013. By confirming your first payment, you explicitly request that the service begins immediately and acknowledge that you thereby waive your 14-day cancellation right. This waiver is confirmed via checkbox at the time of payment. Nothing in these Terms affects your statutory rights under applicable consumer law that cannot be waived or excluded.

Taxes:

Prices shown are exclusive of VAT where applicable. You are responsible for any applicable taxes in your jurisdiction.

Outstanding Obligations on Termination:

Termination of your account does not extinguish any payment obligations that accrued prior to termination. Any amounts owed to Zella at the time of account termination remain due and payable.`
    },
    {
      title: "5. Service Suspension and Termination",
      content: `Zella may suspend or restrict access to the Service, with or without notice, in the following circumstances:

Payment-Related Suspension:
Access to Reports, Analytics, and Overview alerts will be restricted if a scheduled payment fails and the company's approved headcount exceeds their remaining paid limit, or if a subscription is cancelled and the paid period has ended.

Security Suspension:
We may suspend access immediately, without prior notice, where reasonably necessary to protect the security, integrity, confidentiality, or availability of the Service or the data processed through it — including in response to a suspected breach, an active attack, unusual access patterns, or a threat to other users' data.

Legal and Regulatory Suspension:
We may suspend or terminate accounts where we reasonably believe continued provision of the Service could expose Zella or any user to legal, regulatory, reputational, or security risk, including where a customer is subject to sanctions, involved in legal proceedings that affect the Service, or where we receive a binding order from a competent authority.

Violation Suspension:
We may suspend or terminate accounts for violations of these Terms or our Acceptable Use Policy, immediately and without notice for serious violations.

Effect of Suspension (Payment-Related):
When access is restricted due to payment failure, the following continue to work without interruption: employee mobile app survey submissions, administrator account access, and team management functions. Access is restored immediately upon successful payment.

Effect of Termination:
On termination of your account, access to the Service is immediately revoked. Account data is handled per our Privacy Policy. No refunds are provided for prepaid services unless legally required.

Zella's Right to Investigate:
We may investigate suspected violations of these Terms and the Acceptable Use Policy, and may review account activity, usage logs, and access patterns for that purpose. This right does not create an obligation to monitor, and Zella is not liable for failing to detect a violation.`
    },
    {
      title: "6. User Obligations and Conduct",
      content: `As a Zella user, you agree to:

1. Provide Accurate Information:
Submit truthful survey responses and keep account information current and accurate.

2. Use the Service Appropriately:
Use Zella only for its intended purpose of workplace wellbeing monitoring. Do not attempt to circumvent security measures or reverse engineer the software.

3. Respect Others:
Do not harass, threaten, or abuse other users. Do not impersonate others or interfere with others' use of Zella.

4. Comply With Laws:
Follow all applicable local, national, and international laws including data protection regulations, employment law, health and safety obligations, and intellectual property rights.

5. Survey Submission Requirements (Mobile App Users):
Submit surveys honestly and accurately. Do not manipulate or falsify survey responses. Do not submit surveys on behalf of another person.

6. Administrator Responsibilities (Dashboard Users):
Protect employee privacy, use team data only for legitimate workplace safety purposes, not attempt to identify individual survey responses, comply with employment and data protection laws, and not misrepresent the purpose of data collection to employees.

Prohibited Uses:

The following are expressly prohibited and may result in immediate account termination:

- Introducing synthetic, automated, or bot accounts into teams to manipulate group composition or attempt to isolate real survey responses
- Observing or recording when employees submit surveys with the intention of identifying individual responses
- Combining Zella data with external data sources to attempt to de-anonymise survey responses
- Submitting false or manipulated survey responses
- Hacking, scraping, or automated data extraction
- Reverse engineering, decompiling, or disassembling the platform
- Using Zella to build a competing product
- Any activity that violates applicable law

Any harm arising from conduct prohibited under this section is the liability of the customer, not Zella.`
    },
    {
      title: "7. Anonymity Limitations",
      content: `Zella's Technical Safeguards:

Zella enforces a minimum team size of 5 approved employees before any aggregated data is surfaced. This is a technical control representing a reasonable and proportionate measure to protect employee anonymity.

Known Limitations:

Organisations acknowledge that anonymity may be affected by factors outside Zella's control, including:
- Workforce composition (very small teams)
- Attendance patterns (e.g. where most team members are absent and few surveys are submitted in a period)
- Operational knowledge held by the administrator (e.g. knowledge of shift patterns or working arrangements)
- External information about individual employees beyond the platform
- Unusual submission timing or frequency patterns

These edge cases are inherent to any team-level survey system and do not constitute a failure by Zella. Organisations are responsible for assessing whether the platform is appropriate for deployment in their specific circumstances, including where teams are very small or where attendance patterns create a risk of inference.

Zella accepts no responsibility for anonymity failures that result from customer conduct, workforce composition, or information held outside the platform. Conduct intended to exploit these limitations is expressly prohibited under our Acceptable Use Policy.`
    },
    {
      title: "8. Data Rights and Obligations",
      content: `Data Ownership:

You retain ownership of your personal information. You grant Zella a licence to process data as described in our Privacy Policy. Anonymous and aggregated survey data becomes part of Zella's datasets for research and service improvement purposes.

Employee Users Must:
- Understand that survey records contain your user ID at the database level (though never exposed to your employer through any product interface)
- Not attempt to identify other users' survey responses

Administrator Users Must:
- Use team data only for legitimate workplace safety purposes
- Not attempt to de-anonymise survey responses
- Comply with UK GDPR and all applicable data protection laws
- Inform employees about Zella's use and data practices before deployment
- Not use data for discriminatory purposes or individual employment decisions

Employee Notice Responsibility:

The organisation is solely responsible for providing all employee-facing notices, transparency information, lawful bases for processing, workplace consultations (where required by employment law), consents (where required), and workplace communications relating to the deployment of Zella. Zella's Privacy Policy may be shared with employees as supplementary information but does not substitute for the organisation's own legal obligations as data controller.

Data Processing Agreement:

By using Zella to collect employee data, your organisation acts as the data controller for that employee data. Zella acts as the data processor. This relationship is governed by our Data Processing Agreement at zellawellbeing.com/terms/data-processing-agreement, which forms part of these Terms.

Data Export Limitation:

Once data is exported from the Zella platform (for example, as a downloaded report or data export), Zella is not responsible for the security, storage, transmission, use, or onward transfer of that exported data. The organisation is solely responsible for exported data from the point of export.

For full details of data rights, retention, and security see our Privacy Policy.`
    },
    {
      title: "9. Confidentiality",
      content: `Each party agrees to keep confidential any non-public information of the other party disclosed in connection with these Terms ("Confidential Information").

Zella's Confidential Information includes but is not limited to: source code, security architecture, algorithms, scoring methodologies, pricing structures not publicly listed, technical documentation, and business plans.

Customer's Confidential Information includes but is not limited to: employee data, company access codes, and internal business information shared with Zella.

Obligations:

Each party agrees to:
- Use Confidential Information only for the purposes of these Terms
- Not disclose Confidential Information to third parties without prior written consent
- Protect Confidential Information using at least the same care it uses for its own confidential information, and in any event no less than reasonable care

Exceptions:

Confidentiality obligations do not apply to information that:
- Is or becomes publicly available through no fault of the receiving party
- Was already known to the receiving party at the time of disclosure
- Is received from a third party without restriction
- Is required to be disclosed by law, court order, or regulatory requirement — in which case the disclosing party will give reasonable advance notice where permitted

User Confidentiality:

Users shall not disclose non-public information regarding the Service, pricing, security architecture, algorithms, source code, or technical documentation to third parties, except as required by law.`
    },
    {
      title: "10. Intellectual Property Rights",
      content: `Zella's Intellectual Property:

All rights, title, and interest in Zella belong to us, including the Zella name, logo, and trademarks, software, code, and algorithms, dashboard interface and mobile app design, survey questions and scoring methodologies, documentation and user guides, and aggregated data insights and analytics.

Your Limited Licence:

We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use Zella solely for workplace wellbeing monitoring in accordance with these Terms.

You May NOT:
- Copy, modify, or distribute Zella's software
- Reverse engineer, decompile, or disassemble the platform
- Remove copyright or proprietary notices
- Use Zella's trademarks without permission
- Create derivative works based on Zella
- Sell, rent, lease, or sublicence access to Zella
- Use Zella to build a competing product

User-Generated Content:

Survey Responses: You retain ownership of your survey data input. You grant Zella an irrevocable, perpetual, royalty-free licence to process, aggregate, and use anonymised survey data for platform improvement and research purposes.

Feedback and Suggestions: Any feedback, suggestions, or ideas you provide to Zella become our property. We may use feedback without compensation or attribution. You waive any rights to feedback you provide.

Copyright Infringement:

If you believe content on Zella infringes your copyright, email legal@zellawellbeing.com with a description of the copyrighted work, the location of the allegedly infringing material, your contact information, and a statement of good faith belief.`
    },
    {
      title: "11. Service Availability and Modifications",
      content: `No Service Level Agreement:

Unless expressly agreed in writing in a separate commercial agreement, no service level agreement, uptime commitment, response time commitment, or support guarantee applies to the Service. We strive to provide reliable service but do not guarantee uninterrupted or error-free operation, specific uptime percentages, availability at all times, or compatibility with all devices or browsers.

Planned Maintenance:
We may perform scheduled maintenance and will attempt to provide advance notice. Service may be unavailable during maintenance windows.

Unplanned Downtime:
Service interruptions may occur due to technical issues, security incidents, or force majeure. We will work to restore service as quickly as possible. No compensation is provided for downtime.

Service Modifications:

We reserve the right to modify features, functionality, or interface, add or remove features, change survey questions or scoring algorithms, update technical requirements, and discontinue the service with 90 days notice for paid subscribers. Material changes will be communicated with 30 days advance notice.`
    },
    {
      title: "12. Disclaimers and Limitations of Liability",
      content: `SERVICE PROVIDED "AS IS":

ZELLA IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, OR COMPLETENESS OF DATA, OR UNINTERRUPTED OR ERROR-FREE OPERATION.

Medical and Safety Disclaimer:

ZELLA IS NOT A MEDICAL DEVICE AND DOES NOT DIAGNOSE, TREAT, CURE, OR PREVENT ANY DISEASE OR MEDICAL CONDITION. ZELLA DOES NOT MAKE AND MUST NOT BE USED TO MAKE EMPLOYMENT, STAFFING, SCHEDULING, FITNESS-FOR-WORK, TRANSPORTATION, OPERATIONAL, OR HEALTH AND SAFETY DECISIONS. ALL SUCH DECISIONS REMAIN SOLELY THE RESPONSIBILITY OF THE ORGANISATION AND ITS QUALIFIED PERSONNEL. SURVEY RESULTS, TIREDNESS SCORES, AND TEAM ANALYTICS ARE ALGORITHMIC ESTIMATES ONLY AND MAY BE INACCURATE OR UNSUITABLE FOR PARTICULAR CIRCUMSTANCES.

No Professional Advice:

ZELLA DOES NOT PROVIDE MEDICAL, LEGAL, EMPLOYMENT, HEALTH AND SAFETY, OR OTHER PROFESSIONAL ADVICE. YOU SHOULD CONSULT APPROPRIATE QUALIFIED PROFESSIONALS FOR SUCH ADVICE.

Accuracy Disclaimer:

WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR FITNESS FOR PURPOSE OF SURVEY SCORING ALGORITHMS, TIREDNESS PREDICTIONS, OR TEAM-LEVEL ANALYTICS.

Third-Party and Export Disclaimer:

WE ARE NOT RESPONSIBLE FOR: ACTIONS TAKEN BY EMPLOYERS BASED ON TEAM DATA; DECISIONS MADE USING ZELLA DATA; RESEARCH CONDUCTED BY THIRD PARTIES USING ANONYMISED DATASETS; OR THE SECURITY, USE, OR ONWARD TRANSFER OF DATA EXPORTED FROM THE PLATFORM.

Limitation of Liability:

TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZELLA, ITS DIRECTORS, EMPLOYEES, AFFILIATES, AND PARTNERS SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, LOST PROFITS, REVENUE, DATA, OR OPPORTUNITIES, BUSINESS INTERRUPTION, LOSS OF GOODWILL, OR ANY DAMAGES ARISING FROM USE OR INABILITY TO USE ZELLA.

Liability Cap — Business Customers:

FOR BUSINESS CUSTOMERS, OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS SHALL NOT EXCEED THE TOTAL FEES PAID BY THAT CUSTOMER TO ZELLA IN THE 12 MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.

Liability Cap — Consumer Users:

FOR INDIVIDUAL CONSUMERS (PERSONS ACTING OUTSIDE THEIR TRADE OR BUSINESS), OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS SHALL NOT EXCEED THE GREATER OF THE FEES PAID IN THE 12 MONTHS PRECEDING THE CLAIM OR £100 (ONE HUNDRED POUNDS).

Exceptions to Limitations:

These limitations do not apply to: death or personal injury caused by Zella's negligence; fraud or fraudulent misrepresentation by Zella; or any other liability that cannot be excluded or limited under applicable law.

Jurisdictional Variations:

Some jurisdictions do not permit exclusion of implied warranties or limitation of liability for incidental or consequential damages. In such jurisdictions our liability is limited to the maximum extent permitted by law.

Indemnification:

You agree to indemnify, defend, and hold harmless Zella and its officers, directors, employees, and agents from any claims, losses, damages, liabilities, and expenses (including legal fees) arising from your violation of these Terms, your violation of any rights of another party, your use or misuse of Zella, your violation of applicable laws or regulations, or content you submit through Zella.

For Administrators: You agree to indemnify Zella from claims arising from your organisation's use of team data, decisions made based on Zella analytics, employment disputes related to Zella, failure to comply with employment or data protection laws, failure to provide employees with adequate notice, or violations of employee privacy or rights.`
    },
    {
      title: "13. Survival of Terms",
      content: `The following provisions survive termination or expiry of these Terms for any reason:

- Section 4 (Billing and Payment) — with respect to any outstanding payment obligations accrued prior to termination
- Section 9 (Confidentiality) — for 3 years following termination
- Section 10 (Intellectual Property Rights) — indefinitely
- Section 12 (Disclaimers and Limitations of Liability) — indefinitely
- Section 13 (Survival of Terms) — indefinitely
- Section 14 (Dispute Resolution) — indefinitely
- Section 15 (General Provisions) — indefinitely
- Any provision that by its nature is intended to survive termination

Termination does not affect any rights, obligations, or liabilities that have already accrued prior to the date of termination.`
    },
    {
      title: "14. Dispute Resolution",
      content: `Informal Dispute Resolution:

Before pursuing formal action, you agree to contact us at legal@zellawellbeing.com, describe your concern in detail, propose a reasonable solution, and allow 30 days for us to respond and attempt resolution. We are committed to resolving disputes amicably and in good faith.

Formal Disputes:

If informal resolution fails, disputes shall be resolved through the courts of England and Wales, except:
- UK consumers may bring claims in the courts of their local jurisdiction
- Either party may seek injunctive relief in any court of competent jurisdiction where urgent action is required

No Class Actions:

To the extent permitted by applicable law, you agree that disputes must be brought individually, not as a class action, collective action, or representative proceeding. Where class action waivers are prohibited by consumer protection law, this paragraph does not apply.

Governing Law:

These Terms are governed by the laws of England and Wales, excluding conflict of law provisions. Nothing in these Terms limits your rights under UK consumer protection law. If any provision conflicts with mandatory consumer protection law, that law prevails.

These Terms are written in English. If translated, the English version controls in case of conflict.`
    },
    {
      title: "15. General Provisions",
      content: `Entire Agreement:

These Terms, together with the Privacy Policy, Acceptable Use Policy, Cookie Policy, and Data Processing Agreement, constitute the entire agreement between you and Zella. They supersede all prior agreements, understandings, or representations.

Severability:

If any provision is found unenforceable, that provision shall be modified to the minimum extent necessary. Remaining provisions remain in full force and effect.

Waiver:

Failure to enforce any provision does not waive our right to enforce it later. Any waiver must be in writing.

Assignment:

You may not assign or transfer these Terms or your account without our written consent. We may assign these Terms to affiliated companies, acquirers in a merger or acquisition, or any successor entity, with notice to you.

No Third-Party Beneficiaries:

These Terms are solely for your and our benefit. No third party has rights to enforce these Terms, except as required under the Contracts (Rights of Third Parties) Act 1999 where expressly stated.

Force Majeure:

We are not liable for failure to perform due to circumstances beyond our reasonable control, including acts of God, war, terrorism, civil unrest, government actions or regulations, internet or telecommunications failures, power failures, industrial disputes, or pandemic.

Notices:

Notices to you: email to address on your account, or in-app notification. Deemed received 24 hours after sending.
Notices to us: email legal@zellawellbeing.com. Deemed received on the next business day after sending.

Relationship:

These Terms do not create an employment relationship, partnership, joint venture, agency relationship, or franchise relationship between you and Zella. You and Zella are independent parties.

Electronic Signatures:

You agree that electronic signatures are valid and binding. These Terms constitute a "writing" for legal purposes and are enforceable as a written contract.

Contact:
Legal: legal@zellawellbeing.com
Privacy: privacy@zellawellbeing.com
General: use website contact form`
    },
    {
      title: "16. Additional Terms for Administrators",
      content: `If you create an organisation account, you represent and warrant that you have authority to bind your organisation to these Terms, including the Data Processing Agreement.

Administrator Obligations:

You agree to:
- Maintain accurate organisation information
- Manage team memberships responsibly
- Protect employee privacy and confidentiality
- Not attempt to identify individual survey responses
- Inform employees about Zella's use and data collection before or at the point of deployment
- Ensure your deployment of Zella complies with applicable employment law, including any employee consultation obligations
- Comply with UK GDPR and all other applicable laws
- Not use data for discriminatory purposes
- Provide or rely on a lawful basis under UK GDPR for processing employee data through Zella

Data Usage Restrictions:

You may NOT:
- Share team data with unauthorised third parties
- Use data for purposes other than workplace safety and wellbeing
- Attempt to de-anonymise survey responses
- Retaliate against teams or individuals based on survey patterns
- Make individual employment decisions based on anonymous team data
- Export data without proper security safeguards
- Misrepresent to employees how their data is used or what administrators can see
- Use Zella data as the sole or primary basis for any workplace safety or staffing decision

Organisation Liability:

Your organisation is responsible for how team data is used internally, compliance with employment and health and safety laws, obtaining necessary employee consent or lawful basis for processing, protecting exported data, and your administrators' compliance with these Terms.

Indemnification (Organisations):

Organisations agree to indemnify Zella from claims arising from improper use of team data, decisions made based on Zella analytics, employment disputes related to Zella, violations of employee privacy rights, failure to obtain necessary consents or provide required notices, or misrepresentation to employees about data usage.`
    },
    {
      title: "17. Beta Features and Experimental Services",
      content: `We may offer beta, pilot, or experimental features ("Beta Features") marked as "Beta", "Pilot", "Experimental", or similar.

Beta Features:
- May be incomplete, inaccurate, or unreliable
- May be discontinued at any time without notice
- Are not subject to any service availability or uptime guarantee
- Are provided "AS IS" with no warranties of any kind

By opting in to Beta Features, you acknowledge they are experimental, accept additional risk of errors, data inaccuracy, or data loss, and agree to provide feedback. Any feedback about Beta Features becomes Zella's property.

We may remove Beta Features, limit access to them, or require a separate agreement for certain Beta Features at any time without liability.`
    }
  ];

  const summary = {
    title: "Quick Summary",
    content: `These Terms of Service govern your use of Zella's workplace wellbeing platform.

Key points:
- Zella is free for up to 10 employees. Beyond that, £5/employee/month or £54/employee/year.
- Payments are taken in advance via Stripe. Except where required by law, payments are non-refundable.
- Consumer users (individuals) have a 14-day cancellation right, waived at checkout by requesting immediate service.
- If a payment fails, affected employee access is removed immediately and restored on payment.
- Zella must not be used as the sole basis for any staffing, scheduling, fitness-for-work, or safety decision. All such decisions remain the organisation's responsibility.
- Survey scores are algorithmic estimates only and may be inaccurate.
- Organisations are solely responsible for providing employee notices about Zella before deployment.
- Introducing bots or fake users to attempt de-anonymisation is strictly prohibited.
- Survey records contain a user ID at the database level, but are never exposed to employers — only team averages are shown.
- You must be 18+ to use Zella.
- Governed by the laws of England and Wales.

Questions? Contact legal@zellawellbeing.com`
  };

  return (
    <TermsLayout>
      <h1>Terms of Service</h1>
      <p className="last-updated">Last Updated: June 2026</p>

      {sections.map((section, index) => (
        <section key={index}>
          <h2>{section.title}</h2>
          <p style={{ whiteSpace: 'pre-line' }}>{section.content}</p>
        </section>
      ))}

      <section className="summary-section">
        <h2>{summary.title}</h2>
        <p style={{ whiteSpace: 'pre-line' }}>{summary.content}</p>
      </section>

      <footer className="policy-footer">
        <p><strong>Version: 3.0</strong></p>
        <p>Effective Date: June 2026</p>
        <p>Governing Law: England and Wales</p>
        <p><em>Zella — Workplace Wellbeing Monitoring</em></p>
      </footer>
    </TermsLayout>
  );
}

export default TermsOfService;